import { Publishable } from './Publishable';
import { DashThumbnailDTO } from './DashThumbnail';
import { NameTuple } from '../FilterTuple';

export type ArticleStripContainerName =
    | 'news-header'
    | 'html-content-block'
    | 'reader'
    | 'product-series-wheel'
    | 'product-series-grid'
    | 'browse-genre-list'
    | 'chapter-wheel'
    | 'spotlight-series'
    | 'news-wheel'
    | 'continue-reading-grid'
    | 'reader-benefits'
    | 'grouped-item-collection'
    | 'contact-form'
    | 'support-center-page-links'
    | 'newsletter-signup'
    | 'spacer'
    | 'upcoming-releases-wheel'
    | 'rewards'
    | 'carousel'
    | 'html-component';

export class HtmlContent {
    hasChanged: boolean;
    content: string;
    asText: () => string = () => this.content;
}

function isNullEmptyOrUndefined(value: string): boolean {
    return !value || value.trim() === '';
}

export class DashNewsItem extends Publishable {
    isLocked: boolean;
    thumbnail: DashThumbnailDTO;
    firstPublishDate: string;
    title: string;
    summary: string;
    style?: string;
    articleStrips?: ArticleStrip[];
    readableUrl: string;
    previewReadableUrl: string;
    pageType: string;
    ipTags: NameTuple[];
    statusHistory: string[];

    getOriginal(): DashNewsItem {
        return this.original ? this.original as DashNewsItem : null;
    }

    copyFromOriginal(): void {
        super.copyFromOriginal();
        const item = this.getOriginal();
        this.isLocked = item.isLocked;
        this.title = item.title;
        this.articleStrips = item.articleStrips;
        this.summary = item.summary;
        this.style = item.style;
        this.readableUrl = item.readableUrl;
        this.previewReadableUrl = item.previewReadableUrl;
        this.thumbnail = item.thumbnail;
        this.firstPublishDate = item.firstPublishDate;
        this.pageType = item.pageType;
        this.ipTags = item.ipTags;
        this.statusHistory = item.statusHistory;
    }

    getValidationErrors(): ValidationError[] {
        const errors: ValidationError[] = [];
        if (isNullEmptyOrUndefined(this.title)) {
            errors.push({ message: 'Title is required', address: { fieldId: 'title' } });
        }
        if (this.pageType === 'News' && !this.thumbnail) {
            errors.push({ message: 'Thumbnail is required', address: { fieldId: 'thumbnail' } });
        }
        if (isNullEmptyOrUndefined(this.readableUrl)) {
            errors.push({ message: 'Readable URL is required', address: { fieldId: 'readableUrl' } });
        }
        if (!this.articleStrips) {
            errors.push({ message: 'At least one article strip is required', address: { fieldId: 'articleStrips' } });
        } else {
            const stripErrors = this.articleStrips.map((strip, stripIndex) => strip.getValidationErrors({ stripIndex })).flat();
            errors.push(...stripErrors);
        }
        return errors;
    }


    canPublish(): boolean {
        return this.getValidationErrors().length === 0;
    }

    hasChanged(): boolean {
        if (!super.hasChanged()) {
            const orig = this.getOriginal();
            return (orig.title !== this.title
                || orig.articleStrips !== this.articleStrips
                || orig.summary !== this.summary
                || orig.style !== this.style
                || orig.readableUrl !== this.readableUrl
                || orig.previewReadableUrl !== this.previewReadableUrl
                || orig.thumbnail?.localPath !== this.thumbnail?.localPath
                || orig.ipTags !== this.ipTags);
        }

        return true;
    }

    initialize(pageType: string): void {
        const orig = new DashNewsItem();
        orig.isLocked = false;
        orig.status = Publishable.STATUS_NEW;
        orig.statusHistory = [orig.status];
        this.original = orig;
        this.copyFromOriginal();
        this.status = Publishable.STATUS_DRAFT;
        this.statusHistory = [this.status];
        this.pageType = pageType;
    }


    toApi(): DashNewsItemDTO {
        return Object.assign({}, this, {
            articleStrips: this.articleStrips?.map(strip => strip.toApi()),
        });
    }

    static createFromApi(dto: DashNewsItemDTO): DashNewsItem {
        if (!dto) return null;
        return Object.assign(new DashNewsItem(), dto, {
            articleStrips: dto.articleStrips?.map(ArticleStrip.createFromApi),
        });
    }

    cloneAsDraft(): DashNewsItem {
        const item = new DashNewsItem();
        item.initialize(this.pageType);

        item.title = this.title + ' (Copy)';
        item.readableUrl = this.readableUrl;

        if (item.readableUrl.endsWith('/')) {
            item.readableUrl = item.readableUrl.slice(0, -1) + '-copy/';
        } else {
            item.readableUrl = item.readableUrl + '-copy/';
        }
        item.previewReadableUrl = '';
        item.summary = this.summary;
        item.style = this.style;

        item.thumbnail = this.thumbnail ? { ...this.thumbnail } : undefined;
        item.ipTags = this.ipTags?.map(tag => ({ id: tag.id, name: tag.name })) ?? [];
        item.articleStrips = this.articleStrips?.map(strip => strip.clone()) ?? [];

        return item;
    }

    getListingStatuses(): string[] {
        const statuses: string[] = [];
        if (this.isLastRevisionDraft()) {
            statuses.push(Publishable.STATUS_DRAFT);
        }
        if (this.hasPublishedRevision()) {
            statuses.push(Publishable.STATUS_PUBLISHED);
        }
        return statuses;
    }

    hasPublishedRevision(): boolean {
        return this.statusHistory.includes(Publishable.STATUS_PUBLISHED);
    }

    isLastRevisionDraft(): boolean {
        return this.statusHistory[0] === Publishable.STATUS_DRAFT;
    }
}

export interface DashNewsItemDTO {
    isLocked: boolean;
    thumbnail: DashThumbnailDTO;
    firstPublishDate: string;
    title: string;
    summary: string;
    style?: string;
    articleStrips: ArticleStrip[];
    readableUrl: string;
    previewReadableUrl: string;
    pageType: string;
    ipTags: NameTuple[];
}

export enum ShowStripStatus {
    Any = 'Any',
    LoggedIn = 'LoggedIn',
    LoggedOut = 'LoggedOut',
    LoggedInVerified = 'LoggedInVerified',
    LoggedInUnverified = 'LoggedInUnverified',
    LoggedInSubscribed = 'LoggedInSubscribed',
    LoggedInNotSubscribed = 'LoggedInNotSubscribed',
}

export enum GroupItemType {
    ExpandableEntry = 'ExpandableEntry',
    ButtonGroup = 'ButtonGroup',
}

export enum TextType {
    RawHtml = 'RawHtml',
    TinyMCE = 'TinyMCE',
    SimpleString = 'SimpleString',
}

export class ArticleStrip {
    containerName: ArticleStripContainerName;
    params: ArticleStripParams;
    id: number;
    ordinal: number;
    showStripStatus?: ShowStripStatus;
    htmlAnchor?: string;
    cssClass?: string;

    toApi(): ArticleStripDTO {
        return Object.assign({}, this);
    }

    static createFromApi(dto: ArticleStripDTO): ArticleStrip {
        if (!dto) return null;
        return Object.assign(new ArticleStrip(), dto);
    }

    getValidationErrors(address: Address): ValidationError[] {
        switch (this.containerName) {
            case 'news-header':
                return this.getValidationErrorsNewsHeader(address);
            case 'html-content-block':
                return this.getValidationErrorsHtmlContentBlock(address);
            case 'reader':
                return this.getValidationErrorsReader(address);
            case 'product-series-grid':
            case 'product-series-wheel':
                return this.getValidationErrorsProductSeriesList(address);
            case 'browse-genre-list':
                return this.getValidationErrorsBrowserGenreList(address);
            case 'chapter-wheel':
                return this.getValidationErrorsChapterWheel(address);
            case 'spotlight-series':
                return this.getValidationErrorsSpotlightSeries(address);
            case 'continue-reading-grid':
                return this.getValidationErrorsContinueReadingGrid(address);
            case 'grouped-item-collection':
                return this.getValidationErrorsGroupedItemCollection(address);
            case 'spacer':
                return this.getValidationErrorsSpacer(address);
            case 'rewards':
                return this.getValidationErrorsRewards(address);
            case 'carousel':
                return this.getValidationErrorsCarousel(address);
            case 'html-component':
                return this.getValidationErrorsHtmlComponent(address);
            case 'news-wheel':
            case 'reader-benefits':
            case 'contact-form':
            case 'support-center-page-links':
            case 'newsletter-signup':
            case 'upcoming-releases-wheel':
                return [];
            default:
                return [{ message: 'Unknown container name', address }];
        }
    }

    private getValidationErrorsNewsHeader(address: Address): ValidationError[] {
        const params = this.params as Partial<NewsHeaderStripParams | HeroStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (params.isHeroBanner) {
            const heroParams = params as Partial<HeroStripParams>;
            if (!heroParams.thumbnail) {
                errors.push({ message: 'Thumbnail is required', address: { ...address, fieldId: 'thumbnail' } });
            }
            if (!heroParams.hideInfoBlock) {
                if (!heroParams.title) {
                    errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
                }
                if (!heroParams.subTitle) {
                    errors.push({ message: 'Subtitle is required', address: { ...address, fieldId: 'subTitle' } });
                }
                if (params.externalLink === undefined) {
                    if (!!params.seriesId && !params.buttonText) {
                        errors.push({
                            message: 'Call to action is optional but both text and series must both be present',
                            address: { ...address, fieldId: 'buttonText' },
                        });
                    }
                    if (!!params.buttonText && !params.seriesId) {
                        errors.push({
                            message: 'Call to action is optional but both text and series must both be present',
                            address: { ...address, fieldId: 'seriesId' },
                        });
                    }
                } else {
                    if (!!params.externalLink && !params.buttonText) {
                        errors.push({
                            message: 'Call to action is optional but both text and URL must both be present',
                            address: { ...address, fieldId: 'buttonText' },
                        });
                    }
                    if (!!params.buttonText && !params.externalLink) {
                        errors.push({
                            message: 'Call to action is optional but both text and URL must both be present',
                            address: { ...address, fieldId: 'externalLink' },
                        });
                    }
                }
            }
        }
        return errors;
    }

    private getValidationErrorsHtmlContentBlock(address: Address): ValidationError[] {
        const params = this.params as Partial<HtmlContentBlockStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.content) {
            errors.push({ message: 'Content is required', address: { ...address, fieldId: 'content' } });
        }
        return errors;
    }

    private getValidationErrorsReader(address: Address): ValidationError[] {
        const params = this.params as Partial<ReaderStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.productId) {
            errors.push({ message: 'Product is required', address: { ...address, fieldId: 'productId' } });
        }
        return errors;
    }

    private getValidationErrorsProductSeriesList(address: Address): ValidationError[] {
        if ('items' in this.params) {
            return this.getValidationErrorsCuratedProductSeriesList(address);
        } else {
            return this.getValidationErrorsFilteredProductSeriesList(address);
        }
    }

    private getValidationErrorsCuratedProductSeriesList(address: Address): ValidationError[] {
        const params = this.params as Partial<CuratedProductSeriesListStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.title) {
            errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
        }
/////////////////////////////////
        if (!params.subTitle) {
            errors.push({ message: 'Subtitle is required', address: { ...address, fieldId: 'subTitle' } });
        }
//////////
        if (!params.items || params.items.length === 0) {
            errors.push({ message: 'Items are required', address: { ...address, fieldId: 'items' } });
        }
        const ctaErrors = this.getValidationErrorsCallToAction(address);
        if (ctaErrors.length > 0) {
            errors.push(...ctaErrors);
        }
        return errors;
    }

    private getValidationErrorsFilteredProductSeriesList(address: Address): ValidationError[] {
        const params = this.params as Partial<FilteredProductSeriesListStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.title) {
            errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
        }
/////////////////////////////////////////
        if (!params.subTitle) {
            errors.push({ message: 'Subtitle is required', address: { ...address, fieldId: 'subTitle' } });
        }
//////////////////
        if (!params.filterCriteria) {
            errors.push({ message: 'Filter criteria is required', address: { ...address, fieldId: 'filterCriteria' } });
        } else {
            if (!params.filterCriteria.sort) {
                errors.push({ message: 'Sort is required', address: { ...address, fieldId: 'sort' } });
            }
            if (typeof params.filterCriteria.includeSeries !== 'boolean') {
                errors.push({ message: 'Include series is required', address: { ...address, fieldId: 'includeSeries' } });
            }
            if (typeof params.filterCriteria.newOnly !== 'boolean') {
                errors.push({ message: 'New only is required', address: { ...address, fieldId: 'newOnly' } });
            }
            if (!params.filterCriteria.genres) {
                errors.push({ message: 'Genres are required', address: { ...address, fieldId: 'genres' } });
            }
        }
        const ctaErrors = this.getValidationErrorsCallToAction(address);
        if (ctaErrors.length > 0) {
            errors.push(...ctaErrors);
        }
        return errors;
    }

    private getValidationErrorsBrowserGenreList(address: Address): ValidationError[] {
        const params = this.params as Partial<BrowseGenreListStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.title) {
            errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
        }
        if (!params.items || params.items.length === 0) {
            errors.push({ message: 'Items are required', address: { ...address, fieldId: 'items' } });
        }
        return errors;
    }

    private getValidationErrorsChapterWheel(address: Address): ValidationError[] {
        const params = this.params as Partial<ChapterWheelStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.title) {
            errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
        }
        if (!params.subTitle) {
            errors.push({ message: 'Subtitle is required', address: { ...address, fieldId: 'subTitle' } });
        }
        if (!params.items || params.items.length === 0) {
            errors.push({ message: 'Items are required', address: { ...address, fieldId: 'items' } });
        }
        const ctaErrors = this.getValidationErrorsCallToAction(address);
        if (ctaErrors.length > 0) {
            errors.push(...ctaErrors);
        }
        return errors;
    }

    private getValidationErrorsSpotlightSeries(address: Address): ValidationError[] {
        const params = this.params as Partial<SpotlightSeriesStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
/////////////////////////////////
        if (!params.title) {
            errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
        }
        if (!params.subTitle) {
            errors.push({ message: 'Subtitle is required', address: { ...address, fieldId: 'subTitle' } });
        }
//////////
        if (!params.items || params.items.length === 0) {
            errors.push({ message: 'Items are required', address: { ...address, fieldId: 'items' } });
        } else {
            const itemErrors = params.items
                .map((item, itemIndex) => ArticleStrip.getValidationErrorsSpotlightSeriesItem(item, { ...address, itemIndex }))
                .flat();
            if (itemErrors.length > 0) {
                errors.push(...itemErrors);
            }
        }
        return errors;
    }

    private static getValidationErrorsSpotlightSeriesItem(item: SpotlightSeriesStripItem, address: Address): ValidationError[] {
        const errors: ValidationError[] = [];
        if (!item.id) {
            errors.push({ message: 'Series is required', address: { ...address, fieldId: 'id' } });
        }
        if (!item.informationNote) {
            errors.push({ message: 'Information note is required', address: { ...address, fieldId: 'informationNote' } });
        }
/////////////////////////////////
        if (!item.author) {
            errors.push({ message: 'Author is required', address: { ...address, fieldId: 'author' } });
        }
        if (!item.linkText) {
            errors.push({ message: 'Link text is required', address: { ...address, fieldId: 'linkText' } });
        }
        if (!item.thumbnail) {
            errors.push({ message: 'Thumbnail is required', address: { ...address, fieldId: 'thumbnail' } });
        }
//////////
        return errors;
    }

    private getValidationErrorsContinueReadingGrid(address: Address): ValidationError[] {
        const params = this.params as Partial<ContinueReadingGridStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (params.showCount === undefined || params.showCount === null) {
            errors.push({ message: 'Show count is required', address: { ...address, fieldId: 'showCount' } });
        } else if (isNaN(params.showCount)) {
            errors.push({ message: 'Show count must be a number', address: { ...address, fieldId: 'showCount' } });
        }
        return errors;
    }

    private getValidationErrorsGroupedItemCollection(address: Address): ValidationError [] {
        const params = this.params as Partial<GroupedItemCollectionStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.title) {
            errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
        }
        const firstItemType = (params.groupItems?? [])[0]?.type ?? GroupItemType.ExpandableEntry;
        if (firstItemType === GroupItemType.ExpandableEntry && !params.subTitle) {
            errors.push({ message: 'Subtitle is required', address: { ...address, fieldId: 'subTitle' } });
        }
        if (!params.groupItems || params.groupItems.length === 0) {
            errors.push({ message: 'Group items are required', address: { ...address, fieldId: 'groupItems' } });
        }
        const groupErrors = params.groupItems
            .map((group, groupIndex) => ArticleStrip.getValidationErrorsGroupedItemCollectionGroup(group, { ...address, groupIndex }))
            .flat();
        if (groupErrors.length > 0) {
            errors.push(...groupErrors);
        }
        return errors;
    }

    private static getValidationErrorsGroupedItemCollectionGroup(group: GroupedItemCollectionGroup, address: Address): ValidationError[] {
        const errors: ValidationError[] = [];
        if (!group.groupTitle) {
            errors.push({ message: 'Group title is required', address: { ...address, fieldId: 'groupTitle' } });
        }
        if (!group.groupedItems || group.groupedItems.length === 0) {
            errors.push({ message: 'Grouped items are required', address: { ...address, fieldId: 'groupedItems' } });
        }
        const itemErrors = group.groupedItems
            .map((item, itemIndex) => ArticleStrip.getValidationErrorsGroupedItemCollectionItem(item, group.type, { ...address, itemIndex }))
            .flat();
        if (itemErrors.length > 0) {
            errors.push(...itemErrors);
        }
        return errors;
    }

    private static getValidationErrorsGroupedItemCollectionItem(item: GroupedItemCollectionItem, type: GroupItemType, address: Address): ValidationError[] {
        const errors: ValidationError[] = [];
        if (type === GroupItemType.ExpandableEntry) {
            if (!item.titleQuestion) {
                errors.push({ message: 'Title question is required', address: { ...address, fieldId: 'titleQuestion' } });
            }
            if (!item.textType) {
                errors.push({ message: 'Text type is required', address: { ...address, fieldId: 'textType' } });
            }
            if (!item.answerText) {
                errors.push({ message: 'Answer text is required', address: { ...address, fieldId: 'answerText' } });
            }
        } else {
            if (!item.linkText) {
                errors.push({ message: 'Link text is required', address: { ...address, fieldId: 'linkText' } });
            }
            if (!item.linkUrl) {
                errors.push({ message: 'Link URL is required', address: { ...address, fieldId: 'linkUrl' } });
            }
        }
        return errors;
    }

    private getValidationErrorsRewards(address: Address): ValidationError[] {
        const params = this.params as Partial<RewardsStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.startDate) {
            errors.push({ message: 'Start date is required', address: { ...address, fieldId: 'startDate' } });
        }
        if (!params.endDate) {
            errors.push({ message: 'End date is required', address: { ...address, fieldId: 'endDate' } });
        }
        if (!params.beforeContent) {
            errors.push({ message: 'Before content is required', address: { ...address, fieldId: 'beforeContent' } });
        }
        if (!params.conditionMetContent) {
            errors.push({ message: 'Condition met content is required', address: { ...address, fieldId: 'conditionMetContent' } });
        }
        if (!params.afterContent) {
            errors.push({ message: 'After content is required', address: { ...address, fieldId: 'afterContent' } });
        }
        if (!params.rewardsLinks || params.rewardsLinks.length === 0) {
            errors.push({ message: 'Rewards links are required', address: { ...address, fieldId: 'rewardsLinks' } });
        } else {
            const linkErrors = params.rewardsLinks
                .map((link, linkIndex) => ArticleStrip.getValidationErrorsRewardsLink(link, { ...address, fieldId: 'rewardsLinks', itemIndex: linkIndex }))
                .flat();
            if (linkErrors.length > 0) {
                errors.push(...linkErrors);
            }
        }
        if (!params.rewardsSteps || params.rewardsSteps.length === 0) {
            errors.push({ message: 'Rewards steps are required', address: { ...address, fieldId: 'rewardsSteps' } });
        } else {
            const stepErrors = params.rewardsSteps
                .map((step, stepIndex) => ArticleStrip.getValidationErrorsRewardsStep(step, { ...address, fieldId: 'rewardsSteps', itemIndex: stepIndex }))
                .flat();
            if (stepErrors.length > 0) {
                errors.push(...stepErrors);
            }
        }
        return errors;
    }

    private static getValidationErrorsRewardsLink(link: RewardsLink, address: Address): ValidationError[] {
        const errors: ValidationError[] = [];
        if (!('badgeId' in link) && ('couponId' in link)) {
            errors.push({ message: 'Badge or coupon is required', address });
        }
        return errors;
    }

    private static getValidationErrorsRewardsStep(step: RewardsStep, address: Address): ValidationError[] {
        const errors: ValidationError[] = [];
        if (!step.title) {
            errors.push({ message: 'Title is required', address: { ...address, fieldId: 'title' } });
        }
        if (!step.type) {
            errors.push({ message: 'Type is required', address: { ...address, fieldId: 'type' } });
        }
        if (!step.buttonText) {
            errors.push({ message: 'Button text is required', address: { ...address, fieldId: 'buttonText' } });
        }
        if (step.type === 'OpenLink' && !step.buttonLink) {
            errors.push({ message: 'Button link is required', address: { ...address, fieldId: 'buttonLink' } });
        }
        if (!step.content) {
            errors.push({ message: 'Content is required', address: { ...address, fieldId: 'content' } });
        }
        return errors;
    }

    private getValidationErrorsSpacer(address: Address): ValidationError[] {
        const params = this.params as Partial<SpacerStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (params.height === undefined || params.height === null) {
            errors.push({ message: 'Height is required', address: { ...address, fieldId: 'height' } });
        } else if (isNaN(params.height)) {
            errors.push({ message: 'Height must be a number', address: { ...address, fieldId: 'height' } });
        }
        return errors;
    }

    private getValidationErrorsCallToAction(address: Address): ValidationError[] {
        const params = this.params as Partial<{ callToActionText?: string; callToActionUrl?: string }>;
        const errors: ValidationError[] = [];
        if (!!params.callToActionUrl && !params.callToActionText) {
            errors.push({
                message: 'Call to action is optional but both text and URL must both be present',
                address: { ...address, fieldId: 'callToActionText' },
            });
        }
        if (!!params.callToActionText && !params.callToActionUrl) {
            errors.push({
                message: 'Call to action is optional but both text and URL must both be present',
                address: { ...address, fieldId: 'callToActionUrl' },
            });
        }
        return errors;
    }

    private getValidationErrorsCarousel(address: Address): ValidationError[] {
        const params = this.params as Partial<CarouselStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.carouselId) {
            errors.push({ message: 'Carousel is required', address: { ...address, fieldId: 'carouselId' } });
        }
        return errors;
    }

    private getValidationErrorsHtmlComponent(address: Address): ValidationError[] {
        const params = this.params as Partial<HtmlComponentStripParams> | undefined;
        const errors: ValidationError[] = [];
        if (!params) {
            errors.push({ message: 'Params are required', address });
            return errors;
        }
        if (!params.htmlComponentId) {
            errors.push({ message: 'Component is required', address: { ...address, fieldId: 'htmlComponentId' } });
        }
        return errors;
    }

    clone(): ArticleStrip {
        const strip = new ArticleStrip();
        strip.containerName = this.containerName;
        strip.ordinal = this.ordinal;
        strip.params = this.cloneParams();
        strip.showStripStatus = this.showStripStatus;
        strip.htmlAnchor = this.htmlAnchor;
        strip.cssClass = this.cssClass;
        return strip;
    }

    private cloneParams(): ArticleStripParams {
        switch (this.containerName) {
            case 'news-header':
                return this.cloneNewsHeaderParams();
            case 'html-content-block':
                return this.cloneHtmlContentBlockParams();
            case 'reader':
                return this.cloneReaderParams();
            case 'product-series-grid':
            case 'product-series-wheel':
                return this.cloneProductSeriesListParams();
            case 'browse-genre-list':
                return this.cloneBrowserGenreListParams();
            case 'chapter-wheel':
                return this.cloneChapterWheelParams();
            case 'spotlight-series':
                return this.cloneSpotlightSeriesParams();
            case 'grouped-item-collection':
                return this.cloneGroupedItemCollectionParams();
            case 'spacer':
                return this.cloneSpacerParams();
            case 'rewards':
                return this.cloneRewardsParams();
            case 'continue-reading-grid':
            case 'news-wheel':
            case 'reader-benefits':
            case 'contact-form':
            case 'support-center-page-links':
            case 'newsletter-signup':
            case 'upcoming-releases-wheel':
            case 'carousel':
            case 'html-component':
                return { ...this.params };
        }
    }

    private cloneNewsHeaderParams(): NewsHeaderStripParams {
        const { title, publishDate, thumbnail } = this.params as NewsHeaderStripParams;
        return { title, publishDate, thumbnail: thumbnail ? { ...thumbnail } : undefined };
    }

    private cloneHtmlContentBlockParams(): HtmlContentBlockStripParams {
        const { content } = this.params as HtmlContentBlockStripParams;
        return { content };
    }

    private cloneReaderParams(): ReaderStripParams {
        const { productId } = this.params as ReaderStripParams;
        return { productId };
    }

    private cloneProductSeriesListParams(): CuratedProductSeriesListStripParams | FilteredProductSeriesListStripParams {
        if ('items' in this.params) {
            return this.cloneCuratedProductSeriesListParams();
        } else {
            return this.cloneFilteredProductSeriesListParams();
        }
    }

    private cloneCuratedProductSeriesListParams(): CuratedProductSeriesListStripParams {
        const {
            title,
            subTitle,
            callToActionText,
            callToActionUrl,
            items,
        } = this.params as CuratedProductSeriesListStripParams;
        return {
            title,
            subTitle,
            callToActionText,
            callToActionUrl,
            items: items.map(ArticleStrip.cloneProductSeriesListStripItem),
        };
    }

    private static cloneProductSeriesListStripItem(item: ProductSeriesListStripItem): ProductSeriesListStripItem {
        const { type, id, name } = item;
        return { type, id, name };
    }

    private cloneFilteredProductSeriesListParams(): FilteredProductSeriesListStripParams {
        const {
            title,
            subTitle,
            callToActionText,
            callToActionUrl,
            filterCriteria,
        } = this.params as FilteredProductSeriesListStripParams;
        return {
            title,
            subTitle,
            callToActionText,
            callToActionUrl,
            filterCriteria: { ...filterCriteria, genres: filterCriteria.genres.slice() },
        };
    }

    private cloneBrowserGenreListParams(): BrowseGenreListStripParams {
        const { title, items } = this.params as BrowseGenreListStripParams;
        return { title, items: items.map(ArticleStrip.cloneGenreListStripItem) };
    }

    private static cloneGenreListStripItem(item: BrowseGenreListStripItem): BrowseGenreListStripItem {
        const { type, id, name } = item;
        return { type, id, name };
    }

    private cloneChapterWheelParams(): ChapterWheelStripParams {
        const { title, subTitle, callToActionText, callToActionUrl, items } = this.params as ChapterWheelStripParams;
        return {
            title,
            subTitle,
            callToActionText,
            callToActionUrl,
            items: items.map(ArticleStrip.cloneChapterWheelStripItem),
        };
    }

    private static cloneChapterWheelStripItem(item: ChapterWheelStripItem): ChapterWheelStripItem {
        const { type, id, displayType, thumbnail, seriesName, volumeName, relativeName } = item;
        return {
            type,
            id,
            displayType,
            thumbnail: thumbnail ? { ...thumbnail } : undefined,
            seriesName,
            volumeName,
            relativeName,
        };
    }

    private cloneSpotlightSeriesParams(): SpotlightSeriesStripParams {
        const { title, subTitle, items } = this.params as SpotlightSeriesStripParams;
        return { title, subTitle, items: items.map(ArticleStrip.cloneSpotlightSeriesStripItem) };
    }

    private static cloneSpotlightSeriesStripItem(item: SpotlightSeriesStripItem): SpotlightSeriesStripItem {
        const { informationNote, author, id, seriesTitleOverride, thumbnail, linkText, linkUrl } = item;
        return {
            informationNote,
            author,
            id,
            seriesTitleOverride,
            thumbnail: thumbnail ? { ...thumbnail } : undefined,
            linkText,
            linkUrl,
        };
    }

    private cloneGroupedItemCollectionParams(): GroupedItemCollectionStripParams {
        const { title, subTitle, subTitleLoggedOut, groupItems } = this.params as GroupedItemCollectionStripParams;
        return {
            title,
            subTitle,
            subTitleLoggedOut,
            groupItems: groupItems.map(ArticleStrip.cloneGroupedItemCollectionGroup),
        };
    }

    private static cloneGroupedItemCollectionGroup(group: GroupedItemCollectionGroup): GroupedItemCollectionGroup {
        const { ordinal, type, groupTitle, groupedItems } = group;
        return {
            ordinal,
            type,
            groupTitle,
            groupedItems: groupedItems.map(ArticleStrip.cloneGroupedItemCollectionItem),
        };
    }

    private static cloneGroupedItemCollectionItem(item: GroupedItemCollectionItem): GroupedItemCollectionItem {
        const { ordinal, titleQuestion, textType, answerText, linkText, linkUrl } = item;
        return { ordinal, titleQuestion, textType, answerText, linkText, linkUrl };
    }

    private cloneSpacerParams(): SpacerStripParams {
        const { height } = this.params as SpacerStripParams;
        return { height };
    }

    private cloneRewardsParams(): RewardsStripParams {
        const {
            startDate,
            endDate,
            beforeContent,
            conditionMetContent,
            afterContent,
            rewardsLinks,
            rewardsSteps,
        } = this.params as RewardsStripParams;
        return {
            startDate,
            endDate,
            beforeContent,
            conditionMetContent,
            afterContent,
            rewardsLinks: rewardsLinks.map(ArticleStrip.cloneRewardsLink),
            rewardsSteps: rewardsSteps.map(ArticleStrip.cloneRewardsStep),
        };
    }

    private static cloneRewardsLink(link: RewardsLink): RewardsLink {
        if ('badgeId' in link) {
            return { badgeId: link.badgeId };
        } else {
            return { couponId: link.couponId };
        }
    }

    private static cloneRewardsStep(step: RewardsStep): RewardsStep {
        const { ordinal, title, type, buttonText, buttonLink, content } = step;
        return { ordinal, title, type, buttonText, buttonLink, content };
    }
}

export interface ArticleStripDTO {
    containerName: ArticleStripContainerName;
    params: ArticleStripParams;
    id: number;
    ordinal: number;
    showStripStatus?: ShowStripStatus;
    htmlAnchor?: string;
    cssClass?: string;
}

export type ArticleStripParams =
    NewsHeaderStripParams
    | HeroStripParams
    | HtmlContentBlockStripParams
    | ReaderStripParams
    | CuratedProductSeriesListStripParams
    | FilteredProductSeriesListStripParams
    | BrowseGenreListStripParams
    | ChapterWheelStripParams
    | GroupedItemCollectionStripParams
    | SpacerStripParams
    | RewardsStripParams
    | CarouselStripParams
    | HtmlComponentStripParams;

export type FieldId =
    keyof Pick<DashNewsItem, 'title' | 'thumbnail' | 'readableUrl' | 'articleStrips'>
    | keyof NewsHeaderStripParams
    | keyof HeroStripParams
    | keyof HtmlContentBlockStripParams
    | keyof ReaderStripParams
    | keyof CuratedProductSeriesListStripParams
    | keyof FilteredProductSeriesListStripParams
    | keyof BrowseGenreListStripParams
    | keyof SpotlightSeriesStripParams
    | keyof SpotlightSeriesStripItem
    | keyof ChapterWheelStripParams
    | keyof GroupedItemCollectionStripParams
    | keyof SpacerStripParams
    | keyof GroupedItemCollectionItem
    | keyof ProductSeriesListStripFilterCriteria
    | keyof ContinueReadingGridStripParams
    | keyof GroupedItemCollectionGroup
    | keyof RewardsStripParams
    | keyof RewardsLink
    | keyof RewardsStep
    | keyof CarouselStripParams
    | keyof HtmlComponentStripParams;

export interface NewsHeaderStripParams {
    isHeroBanner?: false;
    title?: string;
    publishDate?: string;
    thumbnail?: DashThumbnailDTO;
}

export interface HeroStripParams {
    isHeroBanner: true;
    hideInfoBlock: boolean;
    thumbnail: DashThumbnailDTO;
    colorOverlay?: string;
    // info block params
    title: string;
    subTitle: string;
    authorName?: string;
    buttonText?: string;
    externalLink?: string;
    seriesId?: number;
}

export interface HtmlContentBlockStripParams {
    content: string;
    textType?: TextType;
}

export interface RewardsStripParams {
    startDate: string;
    endDate: string;
    beforeContent: string;
    conditionMetContent: string;
    afterContent: string;
    rewardsLinks: RewardsLink[];
    rewardsSteps: RewardsStep[];
}

export type RewardsLink =
    & { id?: number }
    & (
        | { badgeId: number }
        | { couponId: number }
    );

export interface RewardsStep {
    __reactKey?: number;
    id?: number;
    ordinal: number;
    title: string;
    type: 'Login' | 'OpenLink';
    buttonText: string;
    buttonLink?: string;
    content: string;
}

export interface ReaderStripParams {
    productId: number;
}

export interface CuratedProductSeriesListStripParams {
    title: string;
    subTitle: string;
    callToActionText?: string;
    callToActionUrl?: string;
    items: ProductSeriesListStripItem[];
}

export interface FilteredProductSeriesListStripParams {
    title: string;
    subTitle: string;
    callToActionText?: string;
    callToActionUrl?: string;
    filterCriteria: ProductSeriesListStripFilterCriteria;
}

export interface ProductSeriesListStripItem {
    type: 'Product' | 'Series';
    id: number;
    name: string;
}

export interface ProductSeriesListStripFilterCriteria {
    sort: string;
    includeSeries: boolean;
    newOnly: boolean;
    isOnSale?: boolean;
    hasDownloadablePriceTypeFlag?: number;
    hasShippablePriceTypeFlag?: number;
    genres: number[];
    typeIds: number[];
}

export enum PriceTypeFlag {
    None = 0,
    Free = 1 << 0,
    FreeForRegistered = 1 << 1,
    Paid = 1 << 2,
    NotForSale = 1 << 3,
    SubscriptionOnly = 1 << 4,
    PaidOrSubscription = 1 << 5,
    FreeToReadAndPurchasable = 1 << 6,
    FreeToReadForRegisteredAndPurchasable = 1 << 7,
}

export interface BrowseGenreListStripParams {
    title: string;
    items: BrowseGenreListStripItem[];
}

export interface BrowseGenreListStripItem {
    type: 'Genre';
    id: number;
    name: string;
}

export interface ChapterWheelStripParams {
    title: string;
    subTitle: string;
    callToActionText?: string;
    callToActionUrl?: string;
    items: ChapterWheelStripItem[];
}

export interface ChapterWheelStripItem {
    type: 'Product' | 'Series';
    id: number; // id of the chapter itself
    displayType: 'Product' | 'Series';
    thumbnail?: DashThumbnailDTO;
    seriesName?: string;
    volumeName?: string;
    relativeName?: string;
    publishDate?: string;
}

export interface SpotlightSeriesStripParams {
    title: string;
    subTitle: string;
    items: SpotlightSeriesStripItem[];
}

export interface SpotlightSeriesStripItem {
    informationNote: string;
    author: string;
    seriesTitleOverride: string;
    id: number;
    linkText: string;
    linkUrl: string;
    thumbnail?: DashThumbnailDTO;
}

export interface ContinueReadingGridStripParams {
    showCount: number;
}

export interface GroupedItemCollectionStripParams {
    title: string;
    subTitle: string;
    subTitleLoggedOut?: string;
    groupItems: GroupedItemCollectionGroup[];
}

export interface GroupedItemCollectionGroup {
    __reactKey?: number;
    id?: number;
    ordinal: number;
    type: GroupItemType;
    groupTitle: string;
    groupedItems: GroupedItemCollectionItem[];
    htmlAnchor?: string;
}

export interface GroupedItemCollectionItem {
    __reactKey?: number;
    id?: number;
    ordinal: number;

    // ExpandableEntry
    titleQuestion?: string;
    textType?: TextType;
    answerText?: string;
    htmlAnchor?: string;

    // ButtonGroup
    linkText?: string;
    linkUrl?: string;
}

export interface SpacerStripParams {
    height: number;
}

export interface CarouselStripParams {
    carouselId?: number;
}

export interface HtmlComponentStripParams {
    htmlComponentId?: number;
}

export interface ValidationError {
    message: string;
    address: Address;
}

export interface Address {
    stripIndex?: number;
    groupIndex?: number;
    itemIndex?: number;
    fieldId?: FieldId;
}

 export function isSameAddress(a: Address, b: Address): boolean  {
    return a.stripIndex === b.stripIndex && a.groupIndex === b.groupIndex && a.itemIndex === b.itemIndex && a.fieldId === b.fieldId;
}
